import { Injectable, TemplateRef } from '@angular/core';
import { MatMenu } from '@angular/material/menu';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SiaBaseActionService {
  visibilityActions: any = {};
  visibilityActionsSubject: Subject<any> = new Subject<any>();

  templateActions: any = {};
  templateActionsSubject: Subject<any> = new Subject<any>();

  disabledActions: any = {};
  disabledActionsSubject: Subject<any> = new Subject<any>();

  actionBadges: any = {};
  badgeActionsSubject: Subject<any> = new Subject<any>();

  /**
   * Registry di MatMenu registrati da componenti consumer per una specifica
   * azione di un componente (identificato da guid + componentId + actionId).
   * Permette a `SiaActionsContainer` di legare il bottone dell'azione
   * direttamente al menu corretto tramite `matMenuTriggerFor`, evitando il
   * workaround con swap runtime del menu (che apriva transiente un menu vuoto).
   * Struttura: { [guid]: { [componentId]: { [actionId]: MatMenu } } }
   */
  actionMenus: Record<string, Record<string, Record<string, MatMenu>>> = {};
  actionMenusSubject: Subject<void> = new Subject<void>();

  constructor() {}

  /**
   * Registra un MatMenu da usare come target di `matMenuTriggerFor`
   * per il bottone di una specifica azione di un componente.
   */
  registerActionMenu(guid: string | undefined, componentId: string | undefined, actionId: string, menu: MatMenu) {
    if (!guid || !componentId || !actionId || !menu) return;
    if (!(menu instanceof MatMenu)) {
      console.warn('[SiaBaseActionService.registerActionMenu] tentativo di registrare un valore non-MatMenu', {
        guid,
        componentId,
        actionId,
        value: menu,
      });
      return;
    }
    this.actionMenus[guid] ??= {};
    this.actionMenus[guid][componentId] ??= {};
    this.actionMenus[guid][componentId][actionId] = menu;
    this.actionMenusSubject.next();
  }

  /**
   * Ritorna il MatMenu registrato per (guid, componentId, actionId), se presente.
   */
  getActionMenu(guid: string | undefined, componentId: string | undefined, actionId: string): MatMenu | undefined {
    if (!guid || !componentId || !actionId) return undefined;
    return this.actionMenus[guid]?.[componentId]?.[actionId];
  }

  /**
   * Rimuove la registrazione del MatMenu per una singola azione
   * (guid, componentId, actionId). Usato quando il menu viene distrutto
   * dinamicamente (es. un `@if` nel template lo smonta) e occorre evitare
   * che nel registry resti un riferimento ad un `MatMenu` morto, che
   * verrebbe usato come target di `matMenuTriggerFor`.
   */
  unregisterActionMenu(guid: string | undefined, componentId: string | undefined, actionId: string): void {
    if (!guid || !componentId || !actionId) return;
    const byComp = this.actionMenus[guid];
    if (!byComp) return;
    const byAction = byComp[componentId];
    if (!byAction || !(actionId in byAction)) return;
    delete byAction[actionId];
    if (Object.keys(byAction).length === 0) delete byComp[componentId];
    if (Object.keys(byComp).length === 0) delete this.actionMenus[guid];
    this.actionMenusSubject.next();
  }

  /**
   * Rimuove tutte le registrazioni di MatMenu per (guid, componentId).
   * Chiamato dal SiaActionsContainer al destroy per evitare leak del
   * registry su navigazioni rapide a pagine diverse.
   */
  clearActionMenusForComponent(guid: string | undefined, componentId: string | undefined) {
    if (!guid || !componentId) return;
    const byComp = this.actionMenus[guid];
    if (!byComp || !byComp[componentId]) return;
    delete byComp[componentId];
    if (Object.keys(byComp).length === 0) delete this.actionMenus[guid];
    this.actionMenusSubject.next();
  }

  /**
   * Rimuove una action dalla lista delle action visibili
   * @param componentId il componente al quale viene nascosta la action
   * @param guid il guid al relativo al component al quale viene nascosta l'azione
   * @param actionId l'id dell'action da nascondere
   * @param visible se true, l'action viene mostrata, altrimenti nascosta
   */
  handleAction(componentId: string, guid: string, actionId: string, visible: boolean) {
    if (componentId && guid && actionId) {
      if (this.visibilityActions[guid] == undefined) {
        this.visibilityActions[guid] = {};
      }
      if (this.visibilityActions[guid][componentId] == undefined) {
        this.visibilityActions[guid][componentId] = {};
      }
      this.visibilityActions[guid][componentId][actionId] = visible;
      this.visibilityActionsSubject.next(this.visibilityActions);
    }
  }

  disableAction(componentId: string | undefined, guid: string | undefined, actionId: string, disabled: boolean) {
    if (componentId && guid && actionId) {
      if (this.disabledActions[guid] == undefined) {
        this.disabledActions[guid] = {};
      }
      if (this.disabledActions[guid][componentId] == undefined) {
        this.disabledActions[guid][componentId] = {};
      }
      this.disabledActions[guid][componentId][actionId] = disabled;
      this.disabledActionsSubject.next(this.disabledActions);
    }
  }

  assignActionTemplate(componentId: string, guid: string, actionId: string, template?: TemplateRef<any>) {
    if (componentId && guid && actionId) {
      if (this.templateActions[guid] == undefined) {
        this.templateActions[guid] = {};
      }
      if (this.templateActions[guid][componentId] == undefined) {
        this.templateActions[guid][componentId] = {};
      }
      this.templateActions[guid][componentId][actionId] = template;
      this.templateActionsSubject.next(this.templateActions);
    }
  }

  showActionBadge(componentId: string, guid: string, actionId: string, visible: boolean) {
    if (componentId && actionId && guid) {
      if (this.actionBadges[guid] == undefined) {
        this.actionBadges[guid] = {};
      }
      if (this.actionBadges[guid][componentId] == undefined) {
        this.actionBadges[guid][componentId] = {};
      }
      this.actionBadges[guid][componentId][actionId] = visible;
      this.badgeActionsSubject.next(this.actionBadges);
    }
  }
}
